import {
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@massilia/ui'
import { Link, useLocation } from '@tanstack/react-router'
import { LogOut, Menu } from 'lucide-react'
import { useState } from 'react'

import { useLogout } from '@/hooks/use-logout'

import { CLIENT_TABS, resolveActiveClientTab } from './client-tabs'

interface ClientNavProps {
  /**
   * Nombre de propositions qui attendent une réponse du client. Rendu en
   * pastille sur l'onglet Devis, et seulement s'il est strictement positif —
   * une pastille « 0 » n'appelle à rien.
   */
  pendingQuotesCount?: number
}

/**
 * Navigation de l'espace client : barre d'onglets à partir de `md`, menu burger
 * en dessous. Les deux lisent la même liste (`CLIENT_TABS`) et le même onglet
 * actif, dérivé de l'URL — aucun état de navigation à tenir.
 */
export function ClientNav({ pendingQuotesCount = 0 }: ClientNavProps) {
  const { pathname } = useLocation()
  const activeTab = resolveActiveClientTab(pathname)

  return (
    <>
      <ClientNavTabs activeTab={activeTab} pendingQuotesCount={pendingQuotesCount} />
      <ClientNavSheet activeTab={activeTab} pendingQuotesCount={pendingQuotesCount} />
    </>
  )
}

function ClientNavSheet({
  activeTab,
  pendingQuotesCount,
}: {
  activeTab: string
  pendingQuotesCount: number
}) {
  const [isOpen, setIsOpen] = useState(false)
  const handleLogout = useLogout()

  return (
    <div className="md:hidden" data-testid="client-nav-mobile">
      <Button
        aria-label="Ouvrir le menu"
        className="text-gray-light-600 hover:text-gray-light-900"
        onClick={() => setIsOpen(true)}
        size="icon"
        variant="ghost"
      >
        <Menu className="size-6" />
      </Button>

      <Sheet onOpenChange={setIsOpen} open={isOpen}>
        <SheetContent className="w-72" side="right">
          <SheetHeader>
            <SheetTitle>Mon espace</SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col gap-1 px-4">
            {CLIENT_TABS.map((tab) => (
              <Link
                className={`rounded-lg px-3 py-2.5 text-sm font-medium ${
                  activeTab === tab.segment
                    ? 'bg-brand-600 text-white'
                    : 'text-brand-600/55 hover:bg-brand-50 hover:text-brand-700'
                }`}
                // Même attribut que l'onglet desktop : c'est lui que lit la
                // pastille pour s'inverser sur un fond de marque.
                data-state={activeTab === tab.segment ? 'active' : 'inactive'}
                key={tab.segment}
                onClick={() => setIsOpen(false)}
                to={tab.to}
              >
                {tab.label}
                {tab.segment === 'mes-devis' && <PendingQuotesBadge count={pendingQuotesCount} />}
              </Link>
            ))}
          </nav>
          <div className="mt-auto px-4 pb-6">
            <Button
              className="w-full gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => void handleLogout()}
              variant="outline"
            >
              <LogOut className="size-4" />
              Se déconnecter
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

function ClientNavTabs({
  activeTab,
  pendingQuotesCount,
}: {
  activeTab: string
  pendingQuotesCount: number
}) {
  const handleLogout = useLogout()

  return (
    <div className="hidden items-center gap-3 md:flex" data-testid="client-nav-tabs">
      {/* Les onglets portent la couleur de marque : l'actif en plein, les autres
          dans le même bleu simplement éteint. Une seule teinte pour les trois,
          la hiérarchie se lit à l'intensité — pas à un gris qui les sortirait
          de la marque. */}
      <Tabs value={activeTab}>
        <TabsList className="bg-brand-50">
          {CLIENT_TABS.map((tab) => (
            <TabsTrigger
              asChild
              className="text-brand-600/55 hover:text-brand-700 data-[state=active]:bg-brand-600 data-[state=active]:text-white"
              key={tab.segment}
              value={tab.segment}
            >
              <Link to={tab.to}>
                {tab.label}
                {tab.segment === 'mes-devis' && <PendingQuotesBadge count={pendingQuotesCount} />}
              </Link>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* La déconnexion n'était atteignable que par « Mon profil » : deux clics
          et une page de plus pour un geste qu'on attend dans la barre. */}
      <Button
        aria-label="Se déconnecter"
        className="gap-2 text-gray-light-600 hover:text-gray-light-900"
        onClick={() => void handleLogout()}
        size="sm"
        variant="ghost"
      >
        <LogOut className="size-4" />
        Se déconnecter
      </Button>
    </div>
  )
}

function PendingQuotesBadge({ count }: { count: number }) {
  if (count <= 0) return null

  return (
    // Sur l'onglet actif, le fond est déjà brand : la pastille s'inverse, sinon
    // elle disparaîtrait dedans.
    <span className="ml-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white [[data-state=active]_&]:bg-white [[data-state=active]_&]:text-brand-700">
      {count}
      {/* Sans ça, un lecteur d'écran annonce « Devis 4 » sans dire de quoi 4
          est le compte. */}
      <span className="sr-only">
        {' '}
        proposition{count > 1 ? 's' : ''} en attente de votre réponse
      </span>
    </span>
  )
}
